import { RestSchemas } from '@/contexts/QueryProvider'

export type SharedFileItem = RestSchemas['Messages.Storage.ListSharedFileItem']

export type ApplicationFileTreeFileNode = SharedFileItem & { nodeKind: 'file' }

export type ApplicationFileTreeFolderNode = {
  nodeKind: 'folder'
  name: string
  /** Prefix path for this folder (no trailing slash) */
  path: string
  children: ApplicationFileTreeNode[]
  item?: SharedFileItem
}

export type ApplicationFileTreeNode = ApplicationFileTreeFileNode | ApplicationFileTreeFolderNode

export function transformSharedFilesToTree(files: SharedFileItem[]): ApplicationFileTreeNode[] {
  type MutableFolder = ApplicationFileTreeFolderNode

  const root: MutableFolder = {
    nodeKind: 'folder',
    name: '',
    path: '',
    children: [],
  }

  function findChildFolder(parent: MutableFolder, segment: string): MutableFolder | undefined {
    for (const child of parent.children) {
      if (child.nodeKind === 'folder' && child.name === segment) {
        return child
      }
    }
    return undefined
  }

  function getOrCreateFolder(parent: MutableFolder, segment: string): MutableFolder {
    const existing = findChildFolder(parent, segment)
    if (existing) return existing
    const path = parent.path ? `${parent.path}/${segment}` : segment
    const folder: MutableFolder = {
      nodeKind: 'folder',
      name: segment,
      path,
      children: [],
    }
    parent.children.push(folder)
    return folder
  }

  function ensureFolder(item: SharedFileItem) {
    // blobName is like "namespace/.arq_placeholder" — create folders for all segments except the placeholder leaf
    const segments = (item.blobName ?? '').split('/').filter((s) => s && s !== '.arq_placeholder')
    let parent = root
    for (const segment of segments) {
      parent = getOrCreateFolder(parent, segment)
    }
  }

  function insertItem(item: SharedFileItem) {
    const segments = (item.blobName ?? '').split('/').filter(Boolean)
    if (segments.length === 0) return

    const folderSegments = segments.slice(0, -1)
    let parent = root
    for (const segment of folderSegments) {
      parent = getOrCreateFolder(parent, segment)
    }
    parent.children.push({ ...item, nodeKind: 'file' })
  }

  for (const item of files) {
    if (item.isDirectory) {
      ensureFolder(item)
    } else {
      insertItem(item)
    }
  }

  function compareNodes(a: ApplicationFileTreeNode, b: ApplicationFileTreeNode): number {
    if (a.nodeKind !== b.nodeKind) return a.nodeKind === 'folder' ? -1 : 1
    const nameA = a.nodeKind === 'folder' ? a.name : (a.fileName ?? '')
    const nameB = b.nodeKind === 'folder' ? b.name : (b.fileName ?? '')
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' })
  }

  function sortTree(folder: MutableFolder) {
    folder.children.sort(compareNodes)
    for (const child of folder.children) {
      if (child.nodeKind === 'folder') sortTree(child)
    }
  }

  sortTree(root)
  return root.children
}
